/** Day logic runs in Asia/Karachi regardless of the phone's setting (RULES.md rule 9). */
export const TIME_ZONE = 'Asia/Karachi';

const isoFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** YYYY-MM-DD of `at` in Karachi. */
export function karachiDate(at: Date = new Date()): string {
  return isoFmt.format(at);
}

/** ISO date `days` before `iso` (calendar arithmetic, no time zone involved). */
export function minusDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d - days));
  return dt.toISOString().slice(0, 10);
}

/** Whole days from `iso` to `today` (both YYYY-MM-DD). */
export function daysBetween(iso: string, today: string): number {
  const a = Date.parse(`${iso}T00:00:00Z`);
  const b = Date.parse(`${today}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * SPEC §5: an entry may be dated on any earlier day (owner's decision, 8 Oct 2026; older days
 * are picked from the calendar) or up to 7 days ahead (7 Oct 2026).
 * The quick-pick chips cover a week back to a week ahead.
 */
export const CHIP_DAYS_BACK = 7;
export const MAX_FUTURE_DAYS = 7;

/** The quick-pick chips, oldest first (today is in the middle). */
export function allowedDates(today: string): string[] {
  return Array.from({ length: CHIP_DAYS_BACK + MAX_FUTURE_DAYS + 1 }, (_, i) => minusDays(today, CHIP_DAYS_BACK - i));
}

/** Latest date an entry may have. */
export const latestDate = (today: string): string => minusDays(today, -MAX_FUTURE_DAYS);

/** A real YYYY-MM-DD date an entry may be saved with (any past day, up to 7 days ahead). */
export function isEntryDate(iso: unknown, today: string): iso is string {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return false;
  return y >= 2000 && iso <= latestDate(today);
}

/** A day near today by weekday ("Sun 4"); an older or later one with its month ("4 Sept"). */
export function entryDayLabel(iso: string, today: string, lang: 'en' | 'ur'): string {
  return Math.abs(daysBetween(iso, today)) <= 6 ? shortDayLabel(iso, lang) : dayMonthLabel(iso, today, lang);
}

/** "4 Sep", or "4 Sep 2025" in another year (Western digits in both languages). */
export function dayMonthLabel(iso: string, today: string, lang: 'en' | 'ur'): string {
  const locale = lang === 'ur' ? 'ur-PK-u-nu-latn' : 'en-GB';
  const sameYear = iso.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(new Date(`${iso}T12:00:00Z`));
}

/** "today" / "yesterday" / "tomorrow" for nearby days, otherwise null (show the weekday). */
export function nearDayKey(iso: string, today: string): 'day.today' | 'day.yesterday' | 'day.tomorrow' | null {
  const d = daysBetween(iso, today);
  return d === 0 ? 'day.today' : d === 1 ? 'day.yesterday' : d === -1 ? 'day.tomorrow' : null;
}

/** When a message was written, in Karachi: "8 Oct, 3:45 pm" (Western digits in both languages). */
export function messageTime(at: string, lang: 'en' | 'ur'): string {
  const locale = lang === 'ur' ? 'ur-PK-u-nu-latn' : 'en-GB';
  return new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(at));
}

/** Short weekday + day number, e.g. "Sun 4" / "اتوار 4" (Western digits in both). */
export function shortDayLabel(iso: string, lang: 'en' | 'ur'): string {
  const locale = lang === 'ur' ? 'ur-PK-u-nu-latn' : 'en-GB';
  const dt = new Date(`${iso}T12:00:00Z`);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: lang === 'ur' ? 'long' : 'short', timeZone: 'UTC' }).format(dt);
  return `${weekday} ${dt.getUTCDate()}`;
}
